import { css, cva } from '@styled-system/css';
import { Mail, Package, Store, TrendingUp } from 'lucide-react';
import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

export const MOBILE_BOTTOM_NAV_HEIGHT = 64;

interface BottomNavItem {
  label: string;
  to: string;
  icon: ReactNode;
  unread?: number;
}

// Floating pill style — détaché du bord bas avec margin, ombre marquée.
// `surface.subtle` au lieu d'`elevated` pour avoir un léger off-white en
// light mode (la pill se distingue de la card surface.default = blanc pur)
// tout en gardant un effet "lift" en dark mode.
const root = css({
  display: { base: 'flex', md: 'none' },
  position: 'fixed',
  // Décollage du bord bas (safe-area inclus pour iOS).
  bottom: 'calc(12px + env(safe-area-inset-bottom))',
  left: '32px',
  right: '32px',
  zIndex: 30,
  height: '52px',
  bg: 'surface.subtle',
  borderRadius: 'full',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.strong',
  boxShadow: 'lg',
  overflow: 'hidden',
});

const tabsRow = css({
  display: 'flex',
  alignItems: 'stretch',
  height: '100%',
  width: '100%',
  paddingX: '2',
});

const tab = cva({
  base: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5',
    paddingY: '1',
    paddingX: '1',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'sans',
    transition: 'color 120ms ease',
  },
  variants: {
    active: {
      true: { color: 'brand.primary' },
      false: { color: 'text.tertiary', _hover: { color: 'text.secondary' } },
    },
  },
});

const tabLabel = css({
  fontSize: '10px',
  fontWeight: 'medium',
  letterSpacing: '-0.1px',
  lineHeight: 1,
});

const iconSlot = css({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const unreadDot = css({
  position: 'absolute',
  top: '-2px',
  right: '-6px',
  minWidth: '14px',
  height: '14px',
  paddingX: '1',
  borderRadius: 'full',
  bg: 'error.default',
  color: 'white',
  fontSize: '10px',
  fontWeight: 'bold',
  fontFamily: 'mono',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
});

/**
 * Mobile-only bottom tab bar. 4 tabs : Produits, Vendeurs, Publiques, Contact.
 * Sticky to the viewport bottom with safe-area inset for iOS notches.
 */
export const MobileBottomNav: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const items: BottomNavItem[] = [
    { label: t('nav.products'), to: '/analysis/products', icon: <Package size={18} strokeWidth={1.75} /> },
    { label: t('nav.vendors'), to: '/analysis/vendors', icon: <Store size={18} strokeWidth={1.75} /> },
    { label: t('nav.trend'), to: '/trend', icon: <TrendingUp size={18} strokeWidth={1.75} /> },
    { label: t('nav.contact'), to: '/contact', icon: <Mail size={18} strokeWidth={1.75} /> },
  ];

  return (
    <nav className={root} aria-label={t('ui.mainNavigation')}>
      <div className={tabsRow}>
        {items.map((item) => {
          const active = location.pathname.startsWith(item.to);
          return (
            <button
              type="button"
              key={item.to}
              className={tab({ active })}
              onClick={() => navigate(item.to)}
              aria-current={active ? 'page' : undefined}
            >
              <span className={iconSlot}>
                {item.icon}
                {item.unread != null && <span className={unreadDot}>{item.unread > 99 ? '99+' : item.unread}</span>}
              </span>
              <span className={tabLabel}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
